import React from "react";
import { commitLocalUpdate, commitMutation, fetchQuery, graphql, readInlineData, useFragment } from "react-relay";
import TextAreaAutoSize from "react-textarea-autosize";

import { faCheck, faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import {
	faChevronDown,
	faClone,
	faPlusCircle,
	faSpinner,
	faThumbsDown,
	faThumbsUp,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import {
	faCaretDown,
	faCaretRight,
	faSquare,
	faThumbsDown as fasThumbsDown,
	faThumbsUp as fasThumbsUp,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import Markdown from "markdown-to-jsx";
import invariant from "tiny-invariant";
import { v4 } from "uuid";

import { AppContext } from "~/common";
import ThemeContext from "~/common/ThemeContext";
import { useID } from "~/lib/hooks";
import { hasOwnProperty } from "~/lib/js";
import { useLazyLoadQuery, useMutationPromise } from "~/lib/relayUtil";
import store from "~/lib/store";
import { withSuspense } from "~/lib/suspense";
import { getFirstUserFacingError } from "~/lib/utils";
import { VirtualKeyboardDetector } from "~/lib/virtualKeyboard";
import WebsocketClient, { GroupEmitter } from "~/lib/websocket";
import { environment } from "~/models";
import { Avatar, toast, Tooltip } from "~/ui";
import HelpCenterLink from "~/ui/HelpCenterLink";
import BetaLabel from "~/ui/labels/BetaLabel";
import { IconButton } from "~/ui/legacyFellowIndex";

import { ReactComponent as CopilotAvatar } from "./css/ask-copilot-avatar.svg";
import { ReactComponent as Sparkles } from "./css/ask-copilot-suggested-question-sparkle.svg";
import emptyStateIllustrationDark from "./css/empty-state-illustration-dark.png";
import emptyStateIllustration from "./css/empty-state-illustration.png";
import { openTranscriptModal } from "./TranscriptModal";
import { copyToClipboard, useAnalyticsTrack } from "./utils";

import type { AskCopilotPanel_ChatMessage_conversation$key } from "~/__generated__/AskCopilotPanel_ChatMessage_conversation.graphql";
import type { AskCopilotPanel_ChatMessage_message$key } from "~/__generated__/AskCopilotPanel_ChatMessage_message.graphql";
import type { AskCopilotPanel_ChatMessage_viewer$key } from "~/__generated__/AskCopilotPanel_ChatMessage_viewer.graphql";
import type { AskCopilotPanel_EmptyState_conversation$key } from "~/__generated__/AskCopilotPanel_EmptyState_conversation.graphql";
import type { AskCopilotPanel_groupToolUsesWithResponse_message$key } from "~/__generated__/AskCopilotPanel_groupToolUsesWithResponse_message.graphql";
import type { AskCopilotPanel_RatingButtons_message$key } from "~/__generated__/AskCopilotPanel_RatingButtons_message.graphql";
import type { AskCopilotPanel_SuggestedQuestionButton_suggestedQuestion$key } from "~/__generated__/AskCopilotPanel_SuggestedQuestionButton_suggestedQuestion.graphql";
import type { AskCopilotPanel_ToolUseAndResult_result$key } from "~/__generated__/AskCopilotPanel_ToolUseAndResult_result.graphql";
import type { AskCopilotPanel_ToolUseAndResult_use$key } from "~/__generated__/AskCopilotPanel_ToolUseAndResult_use.graphql";
import type { AskCopilotPanel_useAskCopilotSendMessage_conversation$key } from "~/__generated__/AskCopilotPanel_useAskCopilotSendMessage_conversation.graphql";
import type { AskCopilotPanel_useAskCopilotSendMessage_note$key } from "~/__generated__/AskCopilotPanel_useAskCopilotSendMessage_note.graphql";
import type { AskCopilotPanelAddQAndAToNoteMutation } from "~/__generated__/AskCopilotPanelAddQAndAToNoteMutation.graphql";
import type { AskCopilotPanelConversationListener_conversation$key } from "~/__generated__/AskCopilotPanelConversationListener_conversation.graphql";
import type { AskCopilotMessageRole } from "~/__generated__/AskCopilotPanelInner_conversation.graphql";
import type { AskCopilotPanelInner_conversation$key } from "~/__generated__/AskCopilotPanelInner_conversation.graphql";
import type { AskCopilotPanelInner_viewer$key } from "~/__generated__/AskCopilotPanelInner_viewer.graphql";
import type { AskCopilotPanelMeetingTitleFromGuidQuery } from "~/__generated__/AskCopilotPanelMeetingTitleFromGuidQuery.graphql";
import type { AskCopilotPanelRateMessageMutation } from "~/__generated__/AskCopilotPanelRateMessageMutation.graphql";
import type { AskCopilotPanelSendMessageViaConversationMutation } from "~/__generated__/AskCopilotPanelSendMessageViaConversationMutation.graphql";
import type { AskCopilotPanelSendMessageViaNoteMutation } from "~/__generated__/AskCopilotPanelSendMessageViaNoteMutation.graphql";
import type { AskCopilotPanelStopGeneratingMutation } from "~/__generated__/AskCopilotPanelStopGeneratingMutation.graphql";
import type { BillingRendererSubscriptionQuery as QueryType } from "~/__generated__/BillingRendererSubscriptionQuery.graphql";

import styles from "./css/AskCopilotPanel.module.css";

export const ASK_COPILOT_NEW_USER_KEY = "AskCopilot.hasBeenUsed";

export function useTrackAskCopilotOpened() {
	const dispatchAnalyticsTrack = useAnalyticsTrack();

	return React.useCallback(
		(source: string, sourceMeta?: object) => {
			dispatchAnalyticsTrack("[Ask Copilot] Opened", {
				source,
				...sourceMeta,
			});
		},
		[dispatchAnalyticsTrack],
	);
}

function parseAIResponseAsXML(text: string): Document {
	return new DOMParser().parseFromString(text, "text/xml");
}

function SuggestedQuestionButton({
	suggestedQuestion: suggestedQuestionKey,
	sendMessage,
}: {
	suggestedQuestion: AskCopilotPanel_SuggestedQuestionButton_suggestedQuestion$key;
	sendMessage: (content: string, messageId: string, contentForDisplay?: string) => void;
}) {
	const suggestedQuestion = useFragment(
		graphql`
			fragment AskCopilotPanel_SuggestedQuestionButton_suggestedQuestion on SuggestedQuestion {
				id
				content
				contentForDisplay
			}
		`,
		suggestedQuestionKey,
	);

	const dispatchAnalyticsTrack = useAnalyticsTrack();

	const onClick = React.useCallback(() => {
		sendMessage(suggestedQuestion.content, suggestedQuestion.id, suggestedQuestion.contentForDisplay ?? undefined);
		dispatchAnalyticsTrack("[Ask Copilot] Sent suggested question", {
			// eslint-disable-next-line camelcase
			suggested_question_id: suggestedQuestion.id,
		});
	}, [sendMessage, suggestedQuestion, dispatchAnalyticsTrack]);

	const { account } = React.useContext(AppContext);

	return (
		<button
			key={suggestedQuestion.id}
			onClick={onClick}
			className={styles.suggestedQuestionButton}
			disabled={!account?.allowAiFeatures}
		>
			<div className={styles.suggestedQuestionButtonIcon}>
				<Sparkles />
			</div>
			<div className={styles.suggestedQuestionButtonContent}>
				{suggestedQuestion.contentForDisplay ?? suggestedQuestion.content}
			</div>
		</button>
	);
}

function addQAndAToNote(assistantMessageId: string) {
	commitMutation<AskCopilotPanelAddQAndAToNoteMutation>(environment, {
		mutation: graphql`
			mutation AskCopilotPanelAddQAndAToNoteMutation($messageId: ID!) {
				askCopilotAddQAndAToNote(messageId: $messageId) {
					message {
						id
					}
				}
			}
		`,
		variables: { messageId: assistantMessageId },
		onCompleted(response, errors) {
			if (errors?.length || !response.askCopilotAddQAndAToNote) {
				toast.error("Failed to add response to note, please try again later");
			} else {
				toast.success("Response successfully added to note");
			}
		},
	});
}

interface TextMessageContent {
	type: "text";
	text: string;
}
interface ToolUseMessageContent {
	type: "tool_use";
	id: string;
	name: string;
	input: unknown;
}
interface ToolResultMessageContent {
	type: "tool_result";
	tool_use_id: string;
	content: string;
	is_error?: boolean;
}
type MessageContentItem = TextMessageContent | ToolUseMessageContent | ToolResultMessageContent;

function isTextMessageContent(content: unknown): content is TextMessageContent {
	return (
		typeof content === "object" &&
		!!content &&
		"type" in content &&
		content.type === "text" &&
		"text" in content &&
		typeof content.text === "string"
	);
}

function isToolUseMessageContent(content: unknown): content is ToolUseMessageContent {
	return (
		typeof content === "object" &&
		!!content &&
		"type" in content &&
		content.type === "tool_use" &&
		"id" in content &&
		typeof content.id === "string" &&
		"name" in content &&
		typeof content.name === "string" &&
		"input" in content
	);
}

function isToolResultMessageContent(content: unknown): content is ToolResultMessageContent {
	return (
		typeof content === "object" &&
		!!content &&
		"type" in content &&
		content.type === "tool_result" &&
		"tool_use_id" in content &&
		typeof content.tool_use_id === "string" &&
		"content" in content &&
		typeof content.content === "string"
	);
}

function* iterMessageContent(content: unknown): Generator<MessageContentItem> {
	if (!Array.isArray(content)) return;
	for (const item of content) {
		if (isTextMessageContent(item) || isToolUseMessageContent(item) || isToolResultMessageContent(item)) {
			yield item;
		}
	}
}

interface TagInfo {
	start: number;
	end: number;
	tagName: string;
	isClosing: boolean;
	isComplete: boolean;
}

/**
 * Parses "XML" bit by bit and emits content at the top level and within "result" tags.
 */
class StreamingAIResponseParser {
	path: string[] = [];
	buffer = "";
	readonly selections: readonly string[][];

	constructor(...selections: readonly string[][]) {
		this.selections = selections;
	}

	*feed(chunk: string): Generator<string> {
		const text = this.buffer + chunk;
		this.buffer = "";
		yield* this.parseContentOrTag(text);
	}

	private shouldEmit(): boolean {
		return this.selections.some(
			selection => selection.length === this.path.length && selection.every((part, i) => part === this.path[i]),
		);
	}

	private *maybeEmit(text: string): Generator<string> {
		if (this.shouldEmit()) yield text;
	}

	/**
	 * Matches a tag that might not be complete yet, for example:
	 * - <result> or </result>
	 * - <re or </r
	 * - < or </
	 * If the tag isn't complete then it must be followed by the end of the
	 * input, otherwise we don't count it as a tag. This should allow the LLM to
	 * write things like "a < b" without us trying to parse that as XML.
	 */
	private static TAG_RE = /<(\/?)(?:([a-zA-Z][\w-]*)(>|$)|$)/;

	private findTag(text: string): TagInfo | null {
		const match = StreamingAIResponseParser.TAG_RE.exec(text);
		if (!match) return null;
		const [fullMatch, closingSlash, tagName, closeBracket] = match;
		const start = match.index;
		const end = start + fullMatch.length;
		return {
			start,
			end,
			tagName: tagName ?? "",
			isClosing: !!closingSlash,
			isComplete: !!tagName && !!closeBracket,
		};
	}

	private *parseContentOrTag(text: string): Generator<string> {
		const tag = this.findTag(text);
		if (tag) {
			yield* this.parseContentWithTag(tag, text);
		} else {
			yield* this.maybeEmit(text);
		}
	}

	private *parseContentWithTag(tag: TagInfo, text: string): Generator<string> {
		// Emit any text before the open bracket
		yield* this.maybeEmit(text.slice(0, tag.start));

		if (!tag.isComplete) {
			// The tag isn't done yet, so save the state for continuing later when we
			// get the next chunk of text.
			this.buffer = text.slice(tag.start);
			return;
		}

		if (tag.isClosing) {
			// Try to tolerate missing closing tags
			const tagIndex = this.path.lastIndexOf(tag.tagName);
			if (tagIndex !== -1) this.path = this.path.slice(0, tagIndex);
		} else {
			this.path.push(tag.tagName);
		}

		// Parse any given text that comes after the tag
		let rest = text.slice(tag.end + 1);
		// Trim leading newlines
		for (let i = 0; i < rest.length; i += 1) {
			if (rest[i] !== "\n") {
				rest = rest.slice(i);
				break;
			}
		}
		if (rest) yield* this.parseContentOrTag(rest);
	}
}

function RatingButtons({ message: messageKey }: { message: AskCopilotPanel_RatingButtons_message$key }) {
	const message = useFragment(
		graphql`
			fragment AskCopilotPanel_RatingButtons_message on AskCopilotMessage {
				id
				userRating
			}
		`,
		messageKey,
	);

	const [rateMessage, isInFlight] = useMutationPromise<AskCopilotPanelRateMessageMutation>(graphql`
		mutation AskCopilotPanelRateMessageMutation($messageId: ID!, $rating: AskCopilotMessageRating!) @raw_response_type {
			askCopilotRateAiResponse(messageId: $messageId, rating: $rating) {
				message {
					userRating
				}
			}
		}
	`);

	const onThumbsUpClick = React.useCallback(() => {
		if (isInFlight) return;
		rateMessage({
			variables: { messageId: message.id, rating: "POSITIVE" },
			optimisticResponse: {
				askCopilotRateAiResponse: {
					message: {
						id: message.id,
						userRating: "POSITIVE",
					},
				},
			},
		});
	}, [isInFlight, rateMessage, message]);

	const onThumbsDownClick = React.useCallback(() => {
		if (isInFlight) return;
		rateMessage({
			variables: { messageId: message.id, rating: "NEGATIVE" },
			optimisticResponse: {
				askCopilotRateAiResponse: {
					message: {
						id: message.id,
						userRating: "NEGATIVE",
					},
				},
			},
		});
	}, [isInFlight, rateMessage, message]);

	if (message.userRating === "POSITIVE") {
		return <IconButton className={cx(styles.lastAIMessageFooterAction, styles.disabled)} icon={fasThumbsUp} />;
	} else if (message.userRating === "NEGATIVE") {
		return (
			<IconButton
				className={cx(styles.lastAIMessageFooterAction, styles.disabled)}
				icon={fasThumbsDown}
				flip="horizontal"
			/>
		);
	}

	return (
		<>
			<IconButton className={styles.lastAIMessageFooterAction} icon={faThumbsUp} onClick={onThumbsUpClick} />
			<IconButton
				className={styles.lastAIMessageFooterAction}
				icon={faThumbsDown}
				onClick={onThumbsDownClick}
				flip="horizontal"
			/>
		</>
	);
}

const MARKDOWN_OPTIONS: React.ComponentProps<typeof Markdown>["options"] = {
	disableParsingRawHTML: true,
	forceInline: true,
};

const ChatMessage = ({
	viewer: viewerKey,
	conversation: conversationKey,
	message: messageKey,
	showDisclaimer,
	hideButtons,
	userAvatarOnLeft,
}: {
	viewer: AskCopilotPanel_ChatMessage_viewer$key;
	conversation: AskCopilotPanel_ChatMessage_conversation$key;
	message: AskCopilotPanel_ChatMessage_message$key;
	showDisclaimer: boolean;
	hideButtons?: boolean;
	userAvatarOnLeft?: boolean;
}) => {
	const viewer = useFragment(
		graphql`
			fragment AskCopilotPanel_ChatMessage_viewer on Viewer {
				user {
					id
					fullName
					email
					image
					isTopUser
				}
			}
		`,
		viewerKey,
	);

	const conversation = useFragment(
		graphql`
			fragment AskCopilotPanel_ChatMessage_conversation on AskCopilotConversation {
				note {
					id
					__typename
				}
			}
		`,
		conversationKey,
	);

	const isAboutAllMeetings = conversation.note == null;

	const message = useFragment(
		graphql`
			fragment AskCopilotPanel_ChatMessage_message on AskCopilotMessage {
				id
				role
				content
				contentJson
				contentForDisplay
				pending
				isResponse
				...AskCopilotPanel_ToolUseAndResult_use
				...AskCopilotPanel_RatingButtons_message
			}
		`,
		messageKey,
	);

	const [parser] = React.useState(() => new StreamingAIResponseParser([], ["result"]));
	const [parsedContent, setParsedContent] = React.useState<string>(message.role === "USER" ? message.content : "");

	// Every render feed any new message content to the parser.
	const lastFedIndex = React.useRef(0);
	React.useEffect(() => {
		if (message.role !== "ASSISTANT" || lastFedIndex.current === message.content.length) return;
		let newContent = "";
		for (const chunk of parser.feed(message.content.slice(lastFedIndex.current))) {
			newContent += chunk;
		}
		lastFedIndex.current = message.content.length;
		setParsedContent(existing => existing + newContent);
	}, [message, parser]);

	const contentForDisplay = (message.contentForDisplay ?? parsedContent).trim();

	const dispatchAnalyticsTrack = useAnalyticsTrack();

	const onCopyClick = React.useCallback(() => {
		copyToClipboard(contentForDisplay);
		dispatchAnalyticsTrack("[Ask Copilot] Copied response to clipboard", {
			// eslint-disable-next-line camelcase
			ai_message_id: message.id,
		});
	}, [contentForDisplay, dispatchAnalyticsTrack, message.id]);

	const onAddToNoteClick = React.useCallback(() => {
		if (message.role !== "ASSISTANT" || conversation.note?.__typename !== "Note") return;
		addQAndAToNote(message.id);
		dispatchAnalyticsTrack("[Ask Copilot] Added Q&A to note", {
			// eslint-disable-next-line camelcase
			ai_message_id: message.id,
		});
	}, [conversation, message.id, message.role, dispatchAnalyticsTrack]);

	if (message.role === "ASSISTANT" && !contentForDisplay) return null;

	const messageContentNode =
		message.role === "ASSISTANT" ? (
			<Markdown options={MARKDOWN_OPTIONS}>{contentForDisplay}</Markdown>
		) : (
			contentForDisplay
		);

	const userAvatar = (
		<Avatar
			className={cx(styles.avatar, userAvatarOnLeft ? styles.avatarLeft : styles.avatarRight)}
			user={viewer.user}
		/>
	);

	return (
		<div
			className={cx(styles.message, {
				[styles.messageAssistant]: message.role === "ASSISTANT",
				[styles.messageUser]: message.role === "USER",
			})}
		>
			{message.role === "ASSISTANT" && (
				<CopilotAvatar className={cx(styles.avatar, styles.avatarLeft)} title="Ask Fellow" />
			)}
			{message.role === "USER" && userAvatarOnLeft && userAvatar}
			<div className={styles.messageContentContainer}>
				<div
					className={cx(styles.messageContent, {
						[styles.messageContentAssistant]: message.role === "ASSISTANT",
						[styles.messageContentUser]: message.role === "USER",
					})}
				>
					{messageContentNode}
				</div>
				{/* If the message is from the AI but isn't a response to a user
					    message that means it's a message that we sent that's not part of
					    the conversation, which right now would only be a warning about
					    the number of remaining questions they can ask. */}
				{!hideButtons && message.role === "ASSISTANT" && !message.pending && message.isResponse && (
					<div className={styles.lastAIMessageFooter}>
						<div className={styles.lastAIMessageFooterActions}>
							{!isAboutAllMeetings && conversation.note?.__typename === "Note" && (
								<Tooltip content="Add to note">
									<IconButton
										className={styles.lastAIMessageFooterAction}
										icon={faPlusCircle}
										onClick={onAddToNoteClick}
									/>
								</Tooltip>
							)}
							<Tooltip content="Copy">
								<IconButton className={styles.lastAIMessageFooterAction} icon={faClone} onClick={onCopyClick} />
							</Tooltip>
							<RatingButtons message={message} />
						</div>

						{showDisclaimer && (
							<>
								<p className={styles.aiDisclaimer}>AI outputs can be wrong</p>
								<Tooltip
									content={
										"AI may occasionally give inaccurate responses. " +
										"Remember to verify important details before fully relying on them."
									}
								>
									<IconButton className={styles.aiDisclaimerInfoIcon} icon={faInfoCircle} />
								</Tooltip>
							</>
						)}
					</div>
				)}
			</div>
			{message.role === "USER" && !userAvatarOnLeft && userAvatar}
		</div>
	);
};

export function useAskCopilotSendMessageViaConversation(
	conversationKey: AskCopilotPanel_useAskCopilotSendMessage_conversation$key,
) {
	const conversation = useFragment(
		graphql`
			fragment AskCopilotPanel_useAskCopilotSendMessage_conversation on AskCopilotConversation {
				id
			}
		`,
		conversationKey,
	);

	const [commit, isInFlight] = useMutationPromise<AskCopilotPanelSendMessageViaConversationMutation>(graphql`
		mutation AskCopilotPanelSendMessageViaConversationMutation(
			$conversationId: ID!
			$content: String!
			$messageId: String
			$contentForDisplay: String
		) {
			askCopilotSendMessage(
				content: $content
				conversationId: $conversationId
				messageId: $messageId
				contentForDisplay: $contentForDisplay
			) {
				conversation {
					...AskCopilotPanelFragment
				}
			}
		}
	`);

	const sendMessage = React.useCallback(
		(content: string, messageId?: string, contentForDisplay?: string) => {
			return commit({
				variables: {
					conversationId: conversation.id,
					messageId,
					content,
					contentForDisplay,
				},
				onCompleted: (_result, errors) => {
					if (errors) {
						const message = getFirstUserFacingError(errors) ?? "Something went wrong; please try again later.";
						toast.error(message);
					}
				},
				optimisticUpdater(store) {
					const conversationNode = store.get(conversation.id);
					if (!conversationNode) return;

					const id = btoa(`AskCopilotMessage:${v4()}`);
					const newUserMessage = store.create(id, "AskCopilotMessage");
					newUserMessage.setValue(id, "id");
					newUserMessage.setValue("USER", "role");
					newUserMessage.setValue(contentForDisplay ?? content, "content");
					newUserMessage.setValue(false, "pending");
					newUserMessage.setValue(new Date().toISOString(), "createdAt");
					newUserMessage.setValue(false, "isResponse");

					const messages = [...(conversationNode.getLinkedRecords("messages") ?? []), newUserMessage];
					conversationNode.setLinkedRecords(messages, "messages");
				},
			});
		},
		[commit, conversation],
	);

	return [sendMessage, isInFlight] as const;
}

export function useAskCopilotSendMessageViaNote(noteKey: AskCopilotPanel_useAskCopilotSendMessage_note$key) {
	const note = useFragment(
		graphql`
			fragment AskCopilotPanel_useAskCopilotSendMessage_note on Note {
				id
			}
		`,
		noteKey,
	);

	const [commit, isInFlight] = useMutationPromise<AskCopilotPanelSendMessageViaNoteMutation>(graphql`
		mutation AskCopilotPanelSendMessageViaNoteMutation(
			$noteId: ID!
			$content: String!
			$messageId: String
			$contentForDisplay: String
		) {
			askCopilotSendNoteMessage(
				content: $content
				noteId: $noteId
				messageId: $messageId
				contentForDisplay: $contentForDisplay
			) {
				note {
					askCopilotConversation {
						...AskCopilotPanelFragment
					}
				}
			}
		}
	`);

	const sendMessage = React.useCallback(
		(content: string, messageId?: string, contentForDisplay?: string) => {
			return commit({
				variables: {
					noteId: note.id,
					messageId,
					content,
					contentForDisplay,
				},
				onCompleted: (_result, errors) => {
					if (errors) {
						const message = getFirstUserFacingError(errors) ?? "Something went wrong; please try again later.";
						toast.error(message);
					}
				},
			});
		},
		[commit, note],
	);

	return [sendMessage, isInFlight] as const;
}

graphql`
	fragment AskCopilotPanelFragment on AskCopilotConversation {
		id
		messages {
			id
			role
			content
			contentJson
			pending
			createdAt
			isResponse
			contentForDisplay
			...AskCopilotPanel_groupToolUsesWithResponse_message
			...AskCopilotPanel_ToolUseAndResult_use
			...AskCopilotPanel_ToolUseAndResult_result
			...AskCopilotPanel_ChatMessage_message
		}
		suggestedQuestions {
			id
			...AskCopilotPanel_SuggestedQuestionButton_suggestedQuestion
		}
		reachedMessageLimit
	}
`;

const AskCopilotPanelRefreshQuery = graphql`
	query AskCopilotPanelRefreshQuery($conversationId: ID!) {
		node(id: $conversationId) {
			... on AskCopilotConversation {
				...AskCopilotPanelFragment @relay(mask: false)
			}
		}
	}
`;

type WSMessage =
	| { type: "NEW_RESPONSE_PART"; response_part: string; message_id: string }
	| { type: "RESPONSE_COMPLETE"; message_id: string }
	| {
			type: "NEW_MESSAGE";
			message: {
				id: string;
				role: AskCopilotMessageRole;
				content: string;
				content_json: unknown;
				pending: boolean;
				created_at: string;
				is_response: boolean;
			};
	  }
	| { type: "REFETCH" }
	| { type: "NEW_CONTENT_JSON"; content_json: unknown; message_id: string }
	| { type: "something else" };

// Ask Copilot can be open multiple times for the same conversation at once. In
// that case we need to only handle each websocket message once to avoid adding
// each token to the message twice. To do so, we have a registry of "store
// updaters" keyed by conversation ID, each of which has a reference count.
// When a new AskCopilotPanel is rendered for a conversation, it gets/creates
// the StoreUpdater, increments the refcount, and registers a listener for when
// a new message part is received. When that AskCopilotPanel is unmounted, it
// removes the message part listener, decrements the refcount, and deletes the
// store updater if there are no more references. See useAskCopilotConversationListener
// for that logic.
interface StoreUpdater {
	groupEmitter: GroupEmitter;
	listener: (message: WSMessage) => void;
	messagePartListeners: Set<() => void>;
	refcount: number;
}

const storeUpdaters = new Map<string, StoreUpdater>();

function handleWSMessage(wsMessage: WSMessage, conversationId: string, messagePartListeners: readonly (() => void)[]) {
	if (wsMessage.type === "NEW_RESPONSE_PART") {
		commitLocalUpdate(environment, store => {
			const messageNode = store.get(wsMessage.message_id);
			if (!messageNode) return;

			messageNode.setValue(messageNode.getValue("content") + wsMessage.response_part, "content");
		});

		messagePartListeners.forEach(listener => listener());
	} else if (wsMessage.type === "NEW_CONTENT_JSON") {
		commitLocalUpdate(environment, store => {
			const messageNode = store.get(wsMessage.message_id);
			if (!messageNode) return;

			// To set a JSON value we need to use this internal relay API otherwise
			// it will raise an exception at runtime.
			/* eslint-disable no-underscore-dangle */
			// @ts-ignore
			messageNode._mutator.setValue(messageNode._dataID, "contentJson", wsMessage.content_json);
			/* eslint-enable no-underscore-dangle */
		});

		messagePartListeners.forEach(listener => listener());
	} else if (wsMessage.type === "RESPONSE_COMPLETE") {
		commitLocalUpdate(environment, store => {
			const messageNode = store.get(wsMessage.message_id);
			if (!messageNode) return;

			messageNode.setValue(false, "pending");
		});
		messagePartListeners.forEach(listener => listener());
	} else if (wsMessage.type === "NEW_MESSAGE") {
		const { message: newMessageData } = wsMessage;
		commitLocalUpdate(environment, store => {
			let newMessage = store.get(newMessageData.id);
			const alreadyExisted = !!newMessage;
			if (!newMessage) newMessage = store.create(newMessageData.id, "AskCopilotMessage");
			newMessage.setValue(newMessageData.id, "id");
			newMessage.setValue(newMessageData.role, "role");
			newMessage.setValue(newMessageData.content, "content");
			// To set a JSON value we need to use this internal relay API otherwise
			// it will raise an exception at runtime.
			/* eslint-disable no-underscore-dangle */
			// @ts-ignore
			newMessage._mutator.setValue(newMessage._dataID, "contentJson", newMessageData.content_json);
			/* eslint-enable no-underscore-dangle */
			newMessage.setValue(newMessageData.pending, "pending");
			newMessage.setValue(newMessageData.created_at, "createdAt");
			newMessage.setValue(newMessageData.is_response, "isResponse");

			// If the message was already in the relay store it should also be in the
			// conversation's messages list, so don't add it again.
			if (alreadyExisted) return;
			const conversationNode = store.get(conversationId);
			if (!conversationNode) return;
			const messages = [...(conversationNode.getLinkedRecords("messages") ?? []), newMessage];
			conversationNode.setLinkedRecords(messages, "messages");
		});

		messagePartListeners.forEach(listener => listener());
	} else if (wsMessage.type === "REFETCH") {
		refetch();
	}

	function refetch() {
		fetchQuery<QueryType>(
			environment,
			AskCopilotPanelRefreshQuery,
			{ conversationId: conversationId },
			{ fetchPolicy: "network-only" },
		).toPromise();
	}
}

function useAskCopilotConversationListener(
	conversationKey: AskCopilotPanelConversationListener_conversation$key | null,
	onNewMessagePart: () => void,
) {
	const conversation = useFragment(
		graphql`
			fragment AskCopilotPanelConversationListener_conversation on AskCopilotConversation {
				id
				groupName
			}
		`,
		conversationKey,
	);

	React.useEffect(() => {
		if (!conversation) return;

		let updater = storeUpdaters.get(conversation.id);
		if (updater) {
			updater.refcount += 1;
			updater.messagePartListeners.add(onNewMessagePart);
		} else {
			const groupEmitter = WebsocketClient.getInstance().getOrJoinGroup(conversation.groupName);
			groupEmitter.on("message", listener);

			const messagePartListeners = new Set([onNewMessagePart]);
			updater = {
				groupEmitter,
				listener,
				messagePartListeners,
				refcount: 1,
			};

			storeUpdaters.set(conversation.id, updater);

			function listener(message: WSMessage) {
				if (!conversation) return;
				handleWSMessage(message, conversation.id, [...messagePartListeners]);
			}
		}

		return () => {
			const updater = storeUpdaters.get(conversation.id);
			if (updater) {
				updater.refcount -= 1;
				if (updater.refcount === 0) {
					updater.groupEmitter.off("message", updater.listener);
					updater.groupEmitter.close();
					storeUpdaters.delete(conversation.id);
				} else {
					updater.messagePartListeners.delete(onNewMessagePart);
				}
			}
		};
	}, [conversation, onNewMessagePart]);
}

function findLast<T>(arr: readonly T[], predicate: (item: T) => boolean): T | null {
	for (let i = arr.length - 1; i >= 0; i--) {
		if (predicate(arr[i])) return arr[i];
	}
	return null;
}

type GroupedToolResult<T extends AskCopilotPanel_groupToolUsesWithResponse_message$key> =
	| { type: "toolUsesAndResults"; usesAndResults: { use: T; result: T | undefined }[] }
	| { type: "message"; message: T };

// FIXME: Rename
function groupToolUsesWithResponse<T extends AskCopilotPanel_groupToolUsesWithResponse_message$key>(
	messages: readonly T[],
): GroupedToolResult<T>[] {
	function readMessage(messageKey: T): AskCopilotPanel_groupToolUsesWithResponse_message$key[" $data"] {
		return readInlineData(
			graphql`
				fragment AskCopilotPanel_groupToolUsesWithResponse_message on AskCopilotMessage @inline {
					id
					role
					content
					contentForDisplay
					contentJson
				}
			`,
			messageKey,
		);
	}

	function hasTextContent(messageKey: T) {
		const message = readMessage(messageKey);
		const content = message?.content || message?.contentForDisplay;
		if (!content) return false;
		const parser = new StreamingAIResponseParser([], ["result"]);
		let text = "";
		for (const chunk of parser.feed(content)) {
			text += chunk;
		}
		return !!text.trim();
	}

	function isToolUse(messageKey: T) {
		const message = readMessage(messageKey);
		return (
			message?.role === "ASSISTANT" &&
			[...iterMessageContent(message.contentJson)].some(content => isToolUseMessageContent(content))
		);
	}

	function isToolResult(messageKey: T) {
		const message = readMessage(messageKey);
		return (
			message?.role === "USER" &&
			[...iterMessageContent(message.contentJson)].some(content => isToolResultMessageContent(content))
		);
	}

	const result: GroupedToolResult<T>[] = [];
	let currentToolUsesAndResults: { use: T; result: T | undefined }[] = [];

	for (let i = 0; i < messages.length; i++) {
		const message = messages[i];

		if (isToolUse(message)) {
			i += 1; // Move to the next message in the conversation
			const toolResultMessage: T | undefined = messages[i];
			invariant(!toolResultMessage || isToolResult(toolResultMessage), "Expected tool result to follow tool use");

			if (hasTextContent(message)) {
				if (currentToolUsesAndResults.length) {
					result.push({ type: "toolUsesAndResults", usesAndResults: currentToolUsesAndResults });
					currentToolUsesAndResults = [];
				}
				result.push({ type: "message", message });
			}
			currentToolUsesAndResults.push({ use: message, result: toolResultMessage });
		} else {
			if (currentToolUsesAndResults.length) {
				result.push({ type: "toolUsesAndResults", usesAndResults: currentToolUsesAndResults });
				currentToolUsesAndResults = [];
			}
			result.push({ type: "message", message });
		}
	}

	if (currentToolUsesAndResults.length) {
		result.push({ type: "toolUsesAndResults", usesAndResults: currentToolUsesAndResults });
	}
	return result;
}

type ActionItem = {
	text: string;
	dueDate: Date | null;
	updatedAt: Date;
	assignees: { name: string; status: string }[] | null;
};

type ActionItemsResult = {
	noteTitle: string;
	noteId: string;
	actionItems: ActionItem[];
}[];

function parseActionItemsResult(rawContent: string): ActionItemsResult {
	const doc = parseAIResponseAsXML(rawContent);

	const parsedResults = [];
	for (const meetingElement of doc.querySelectorAll("meeting")) {
		const noteTitle = meetingElement.querySelector("note_title")?.textContent;
		const noteId = meetingElement.querySelector("note_id")?.textContent;

		if (!noteTitle || !noteId) continue;

		const actionItems = [];
		for (const actionItemElement of meetingElement.querySelectorAll("action_item")) {
			const text = actionItemElement.querySelector("text")?.textContent;
			const dueDateNode = actionItemElement.querySelector("due_date");
			const dueDate = dueDateNode?.textContent ? new Date(dueDateNode.textContent) : null;
			const updatedAt = new Date(actionItemElement.querySelector("updated_at")?.textContent ?? "");
			const assignees = Array.from(actionItemElement.querySelectorAll("assignee")).map(assignee => ({
				name: assignee.querySelector("name")?.textContent ?? "",
				status: assignee.querySelector("status")?.textContent ?? "",
			}));

			if (!text) continue;
			actionItems.push({ text, dueDate, updatedAt, assignees });
		}

		parsedResults.push({ noteTitle, noteId, actionItems });
	}

	return parsedResults;
}

type SearchMeetingTranscriptsResult = {
	meetingTitle: string;
	meetingDate: Date;
	quotes: string[];
}[];

function parseSearchMeetingTranscriptsResult(rawContent: string): SearchMeetingTranscriptsResult {
	const doc = parseAIResponseAsXML(rawContent);

	const parsedResults = [];
	for (const result of doc.querySelectorAll("search_result")) {
		const meetingTitle = result.querySelector("meeting_title")?.textContent;
		const meetingDateNode = result.querySelector("meeting_date"),
			meetingDate = meetingDateNode?.textContent ? new Date(meetingDateNode.textContent) : null;
		const quotes = Array.from(result.querySelectorAll("quote"))
			.map(quote => quote.textContent ?? "")
			.filter(Boolean);

		if (!meetingTitle || !meetingDate || isNaN(meetingDate.getTime()) || !quotes.length) continue;
		parsedResults.push({ meetingTitle, meetingDate, quotes });
	}

	return parsedResults;
}

type SearchMeetingsResult = {
	meetingTitle: string;
	meetingStartTime: Date;
	meetingEndTime: Date | null;
	quotes?: string[];
	summaries?: string[];
	recapHighlights?: string[];
}[];

function parseSearchMeetingsResult(rawContent: string): SearchMeetingsResult {
	const doc = parseAIResponseAsXML(rawContent);

	const parsedResults = [];
	for (const result of doc.querySelectorAll("meeting")) {
		const meetingTitle = result.querySelector("meeting_title")?.textContent;
		const meetingStartTimeNode = result.querySelector("meeting_start_time"),
			meetingEndTimeNode = result.querySelector("meeting_end_time"),
			meetingStartTime = meetingStartTimeNode?.textContent ? new Date(meetingStartTimeNode.textContent) : null,
			meetingEndTime = meetingEndTimeNode?.textContent ? new Date(meetingEndTimeNode.textContent) : null;

		if (!meetingTitle || !meetingStartTime || isNaN(meetingStartTime.getTime())) continue;

		const quotes = Array.from(result.querySelectorAll("quote"))
			.map(quote => {
				const words = quote.querySelector("words");
				const speaker = quote.querySelector("speaker");

				if (words) {
					// Handle <quote><words>text</words></quote> or <quote><speaker>speaker</speaker><words>text</words></quote>
					return speaker ? `${speaker.textContent}: ${words.textContent}` : (words.textContent ?? "");
				} else {
					// Handle <quote>text</quote>
					return quote.textContent ?? "";
				}
			})
			.filter(Boolean);

		const recapHighlights = Array.from(result.querySelectorAll("recap_highlight"))
			.map(recapHighlight => {
				const words = recapHighlight.querySelector("words");
				const recapSection = recapHighlight.querySelector("recap_section");

				if (words) {
					if (recapSection) {
						// <recap_highlight><recap_section>section name</recap_section><words>text</words></recap_highlight>
						return `${recapSection.textContent}: ${words.textContent}`;
					}

					// Handle <recap_highlight><words>text</words></recap_highlight>
					return words.textContent ?? "";
				} else {
					// Handle <recap_highlight>text</recap_highlight>
					return recapHighlight.textContent ?? "";
				}
			})
			.filter(Boolean);

		const summaries = Array.from(result.querySelectorAll("summary"))
			.map(summary => summary.textContent ?? "")
			.filter(Boolean);

		parsedResults.push({ meetingTitle, meetingStartTime, meetingEndTime, quotes, summaries, recapHighlights });
	}

	return parsedResults;
}

function DetailsSummary({
	summary,
	children,
	className,
	summaryClassName,
}: React.PropsWithChildren<{
	loading?: boolean;
	summary: React.ReactNode;
	className?: string;
	summaryClassName?: string;
}>) {
	const [open, setOpen] = React.useState(false);
	const detailsRef = React.useRef<HTMLDetailsElement>(null);
	const hasChildren = React.Children.count(children) > 0;

	function onToggle(event: React.SyntheticEvent<HTMLDetailsElement>) {
		if (event.target !== detailsRef.current) return;
		setOpen(open => !open);
	}

	return (
		<details ref={detailsRef} onToggle={onToggle} open={open} className={cx(styles.details, className)}>
			<summary className={cx(styles.summary, summaryClassName)}>
				{hasChildren && (
					<FontAwesomeIcon icon={open ? faCaretDown : faCaretRight} fixedWidth className={styles.detailsIcon} />
				)}
				{summary}
			</summary>
			{children}
		</details>
	);
}

function ToolDetails({
	summary,
	loading,
	children,
}: React.PropsWithChildren<{ loading?: boolean; summary: React.ReactNode }>) {
	if (loading) {
		return (
			<p className={cx(styles.summary, styles.toolUseDescription)}>
				<FontAwesomeIcon icon={faSpinner} spin className={styles.detailsIcon} />
				{summary}
			</p>
		);
	}

	if (!children) {
		return <p className={cx(styles.summary, styles.toolUseDescription, styles.toolUseWithoutIcon)}>{summary}</p>;
	}

	return (
		<DetailsSummary summaryClassName={styles.toolUseDescription} summary={summary}>
			{children}
		</DetailsSummary>
	);
}

const meetingDateFormatter = new Intl.DateTimeFormat(undefined, {
	month: "short",
	day: "numeric",
	year: "numeric",
});

const MeetingTitleFromGuid = withSuspense(null, function MeetingTitleFromGuid({ guid }: { guid: string }) {
	const result = useLazyLoadQuery<AskCopilotPanelMeetingTitleFromGuidQuery>(
		graphql`
			query AskCopilotPanelMeetingTitleFromGuidQuery($guid: String!) {
				eventByGuid(guid: $guid) {
					title
					start
				}
			}
		`,
		{ guid },
		{},
	);

	if (!result.eventByGuid) return <>{guid}</>;

	const { title, start } = result.eventByGuid;
	return (
		<>
			{title}
			{start && <> on {meetingDateFormatter.format(new Date(start))}</>}
		</>
	);
});

// Accept whatever after day, even if there's a timezone we don't care.
const DATE_RE = /(\d{4})-(\d{2})-(\d{2}).*/;

/** Parse a date string in the format "YYYY-MM-DD" as a local time date.
 *
 * We need this function because Date parsing in JS works like this:
 * > When the time zone offset is absent, date-only forms are interpreted as a
 * > UTC time and date-time forms are interpreted as local time.
 *
 * This means that `new Date("2024-07-08")` is actually `2024-07-07T20:00:00.000-0400`,
 * so the date we display in the UI might not match what we wanted. Between
 * doing this and something janky like appending `T00:00:00.000` or trying to
 * guess what the user's timezone offset is, this seems to be the least evil.
 */
function parseDateAsLocal(date: string): Date | null {
	const match = DATE_RE.exec(date);
	if (!match) return null;

	const [, yearString, monthString, dayString] = match;
	const year = parseInt(yearString, 10),
		month = parseInt(monthString, 10),
		day = parseInt(dayString, 10);

	return new Date(year, month - 1, day);
}

function ToolUseAndResult({
	use: useKey,
	result: resultKey,
}: {
	use: AskCopilotPanel_ToolUseAndResult_use$key;
	result: AskCopilotPanel_ToolUseAndResult_result$key | undefined;
}) {
	const use = useFragment(
		graphql`
			fragment AskCopilotPanel_ToolUseAndResult_use on AskCopilotMessage {
				contentJson
			}
		`,
		useKey,
	);

	const result = useFragment(
		graphql`
			fragment AskCopilotPanel_ToolUseAndResult_result on AskCopilotMessage {
				contentJson
			}
		`,
		resultKey ?? null,
	);

	const thinking = React.useMemo(() => {
		const text = [...iterMessageContent(use.contentJson)].find(
			(content): content is TextMessageContent => content.type === "text",
		)?.text;
		if (!text) return null;
		if (!text.includes("<thinking>") && !text.includes("<search_quality_reflection>")) return text;
		const doc = parseAIResponseAsXML(`<xml>${text}</xml>`);
		const thinkingNode = doc.querySelector("thinking");
		const searchQualityReflectionNode = doc.querySelector("search_quality_reflection");
		if (thinkingNode?.textContent) return thinkingNode.textContent;
		if (searchQualityReflectionNode?.textContent) return searchQualityReflectionNode.textContent;
		return null;
	}, [use]);

	const toolUsed = [...iterMessageContent(use.contentJson)].find(isToolUseMessageContent);
	if (!toolUsed) return null;

	const thinkingNode = thinking?.trim() && (
		<Tooltip content={thinking.trim()} className={styles.thinkingTooltip}>
			<FontAwesomeIcon icon={faInfoCircle} className={styles.thinkingInfoIcon} />
		</Tooltip>
	);

	switch (toolUsed.name) {
		case "get_action_items": {
			if (result) {
				const toolResult = [...iterMessageContent(result.contentJson)].find(isToolResultMessageContent);
				if (!toolResult) return null;
				if (toolResult.is_error) {
					return <p>Searching Action Items failed</p>;
				}
				const results = parseActionItemsResult(toolResult.content);
				return (
					<ToolDetails summary={<>Searching Action Items</>}>
						<ul className={styles.transcriptSearchResultList}>
							{results.map(({ noteTitle, noteId, actionItems }) => (
								<li key={noteId}>
									<DetailsSummary
										summary={
											<>
												<strong>Meeting Title:</strong> {noteTitle}
											</>
										}
									>
										<ul className={styles.transcriptSearchResultList}>
											{actionItems.map(item => (
												<li key={`${noteId}`}>
													<DetailsSummary
														summary={
															<>
																<strong>Action Item:</strong>&nbsp;{item.text}
															</>
														}
													>
														<div className={styles.actionItemDetails} onClick={() => openTranscriptModal({ noteId })}>
															<p>
																<strong>Last updated:</strong> {meetingDateFormatter.format(item.updatedAt)}
															</p>
															<p>
																<strong>Due on:</strong>{" "}
																{item.dueDate ? meetingDateFormatter.format(item.dueDate) : "no due date set"}
															</p>
															{item.assignees && (
																<>
																	<strong>Assignees:</strong>
																	<ul>
																		{item.assignees.map(({ name, status }) => (
																			<li key={`${name}`}>
																				{name}: <span className={styles.assigneeStatus}>{status}</span>
																			</li>
																		))}
																	</ul>
																</>
															)}
														</div>
													</DetailsSummary>
												</li>
											))}
										</ul>
									</DetailsSummary>
								</li>
							))}
						</ul>
					</ToolDetails>
				);
			} else {
				return <ToolDetails loading summary={<>Searching Action Items</>} />;
			}
		}
		case "search_meeting_transcripts": {
			const query =
				typeof toolUsed.input === "object" &&
				toolUsed.input &&
				hasOwnProperty(toolUsed.input, "query") &&
				typeof toolUsed.input.query === "string"
					? toolUsed.input.query
					: null;
			if (!query) return null;

			if (result) {
				const toolResult = [...iterMessageContent(result.contentJson)].find(isToolResultMessageContent);
				if (!toolResult) return null;
				if (toolResult.is_error) {
					return <p>Searching meeting transcripts for &ldquo;{query}&rdquo; failed</p>;
				}
				const results = parseSearchMeetingTranscriptsResult(toolResult.content);
				return (
					<ToolDetails
						summary={
							<>
								Searched {results.length !== 0 ? <>{results.length} </> : null}meeting transcripts for &ldquo;{query}
								&rdquo;{thinkingNode}
							</>
						}
					>
						<ul className={styles.transcriptSearchResultList}>
							{results.map(({ meetingTitle, meetingDate, quotes }, i) => (
								// eslint-disable-next-line react/no-array-index-key
								<li key={i}>
									<DetailsSummary
										summary={
											<>
												{meetingTitle} on {meetingDateFormatter.format(meetingDate)}
											</>
										}
									>
										<ul className={styles.quoteList}>
											{quotes.map(quote => (
												<li key={quote}>
													<blockquote className={styles.transcriptQuote}>{quote}</blockquote>
												</li>
											))}
										</ul>
									</DetailsSummary>
								</li>
							))}
						</ul>
					</ToolDetails>
				);
			} else {
				return (
					<ToolDetails
						loading
						summary={
							<>
								Searching meeting transcripts for &ldquo;{query}&rdquo;…{thinkingNode}
							</>
						}
					/>
				);
			}
		}

		case "get_meeting_transcript": {
			if (
				typeof toolUsed.input !== "object" ||
				!toolUsed.input ||
				!hasOwnProperty(toolUsed.input, "meeting_id") ||
				typeof toolUsed.input.meeting_id !== "string"
			)
				return null;
			if (result) {
				return (
					<ToolDetails
						summary={
							<>
								Got meeting transcript for <MeetingTitleFromGuid guid={toolUsed.input.meeting_id} />
								{thinkingNode}
							</>
						}
					/>
				);
			}
			return (
				<ToolDetails
					loading
					summary={
						<>
							Getting meeting transcript for <MeetingTitleFromGuid guid={toolUsed.input.meeting_id} />…{thinkingNode}
						</>
					}
				/>
			);
		}
		case "get_meeting_participants": {
			if (
				typeof toolUsed.input !== "object" ||
				!toolUsed.input ||
				!hasOwnProperty(toolUsed.input, "meeting_id") ||
				typeof toolUsed.input.meeting_id !== "string"
			)
				return null;
			if (result) {
				return (
					<ToolDetails
						summary={
							<>
								Got meeting participants for <MeetingTitleFromGuid guid={toolUsed.input.meeting_id} />
								{thinkingNode}
							</>
						}
					/>
				);
			}
			return (
				<ToolDetails
					loading
					summary={
						<>
							Getting meeting participants for <MeetingTitleFromGuid guid={toolUsed.input.meeting_id} />…{thinkingNode}
						</>
					}
				/>
			);
		}
		case "get_meeting_summary": {
			// Check for meeting_ids first (new format), then fall back to meeting_id (old format)
			let meetingIds: string[] = [];

			if (
				typeof toolUsed.input === "object" &&
				toolUsed.input &&
				hasOwnProperty(toolUsed.input, "meeting_ids") &&
				Array.isArray(toolUsed.input.meeting_ids) &&
				toolUsed.input.meeting_ids.length > 0
			) {
				// New format with meeting_ids array
				meetingIds = toolUsed.input.meeting_ids;
			} else if (
				typeof toolUsed.input === "object" &&
				toolUsed.input &&
				hasOwnProperty(toolUsed.input, "meeting_id") &&
				typeof toolUsed.input.meeting_id === "string"
			) {
				// Old format with a single meeting_id
				meetingIds = [toolUsed.input.meeting_id];
			} else {
				return null;
			}

			// Use the first meeting ID for display
			const displayId = meetingIds[0];
			const hasMoreMeetings = meetingIds.length > 1;
			const additionalMeetingsText = hasMoreMeetings
				? ` and ${meetingIds.length - 1} more meeting${meetingIds.length > 2 ? "s" : ""}`
				: "";

			if (result) {
				const toolResult = [...iterMessageContent(result.contentJson)].find(isToolResultMessageContent);
				if (!toolResult) return null;

				if (toolResult.is_error) {
					return <ToolDetails summary={<>Getting meeting summary failed</>} />;
				}

				return (
					<ToolDetails
						summary={
							<>
								Got meeting summary for <MeetingTitleFromGuid guid={displayId} />
								{additionalMeetingsText}
								{thinkingNode}
							</>
						}
					>
						<ul className={styles.meetingSearchResultList}>
							{meetingIds.map((meetingId, _index) => (
								<li key={meetingId}>
									<p className={styles.summary}>
										<MeetingTitleFromGuid guid={meetingId} />
									</p>
								</li>
							))}
						</ul>
					</ToolDetails>
				);
			}
			return (
				<ToolDetails
					loading
					summary={
						<>
							Getting meeting summary for <MeetingTitleFromGuid guid={displayId} />
							{additionalMeetingsText}…{thinkingNode}
						</>
					}
				/>
			);
		}

		case "get_meetings_from_calendar":
		case "search_upcoming_meetings":
		case "search_past_meetings":
		case "search_meetings": {
			const query =
				typeof toolUsed.input === "object" && toolUsed.input && hasOwnProperty(toolUsed.input, "query")
					? toolUsed.input.query
					: null;
			const queryDisplay = query && <> for &ldquo;{query}&rdquo;</>;

			let actionText: {
				thinking: React.ReactNode;
				success: React.ReactNode;
				failure: React.ReactNode;
			};
			switch (toolUsed.name) {
				case "search_upcoming_meetings":
					actionText = {
						thinking: <>Searching upcoming meetings{queryDisplay}…</>,
						success: <>Searched upcoming meetings{queryDisplay}</>,
						failure: <>Searching upcoming meetings{queryDisplay} failed</>,
					};
					break;
				case "search_past_meetings":
					actionText = {
						thinking: <>Searching past meetings{queryDisplay}…</>,
						success: <>Searched past meetings{queryDisplay}</>,
						failure: <>Searching past meetings{queryDisplay} failed</>,
					};
					break;
				case "search_meetings": {
					const rawFromDate =
						typeof toolUsed.input === "object" &&
						toolUsed.input &&
						hasOwnProperty(toolUsed.input, "from_date") &&
						typeof toolUsed.input.from_date === "string"
							? toolUsed.input.from_date
							: null;
					const rawToDate =
						typeof toolUsed.input === "object" &&
						toolUsed.input &&
						hasOwnProperty(toolUsed.input, "to_date") &&
						typeof toolUsed.input.to_date === "string"
							? toolUsed.input.to_date
							: null;
					const fromDate = rawFromDate ? parseDateAsLocal(rawFromDate) : null;
					const toDate = rawToDate ? parseDateAsLocal(rawToDate) : null;

					const fromDateDisplay =
						!!fromDate && !isNaN(fromDate.getTime()) ? meetingDateFormatter.format(fromDate) : rawFromDate;
					const toDateDisplay = !!toDate && !isNaN(toDate.getTime()) ? meetingDateFormatter.format(toDate) : rawToDate;

					const timeRangeParts = [];
					if (fromDateDisplay || toDateDisplay) {
						if (fromDateDisplay === toDateDisplay) {
							timeRangeParts.push(<> on {fromDateDisplay}</>);
						} else if (fromDateDisplay && !toDateDisplay) {
							timeRangeParts.push(<> from {fromDateDisplay} onward</>);
						} else if (!fromDateDisplay && toDateDisplay) {
							timeRangeParts.push(<> up to {toDateDisplay}</>);
						} else {
							timeRangeParts.push(
								<>
									{" "}
									from {fromDateDisplay} to {toDateDisplay}
								</>,
							);
						}
					}

					actionText = {
						thinking: (
							<>
								Searching meetings{timeRangeParts}
								{queryDisplay}…
							</>
						),
						success: (
							<>
								Searched meetings{timeRangeParts}
								{queryDisplay}
							</>
						),
						failure: (
							<>
								Searching meetings{timeRangeParts}
								{queryDisplay} failed
							</>
						),
					};
					break;
				}
				case "get_meetings_from_calendar": {
					const rawFromDate =
						typeof toolUsed.input === "object" &&
						toolUsed.input &&
						hasOwnProperty(toolUsed.input, "from_date") &&
						typeof toolUsed.input.from_date === "string"
							? toolUsed.input.from_date
							: null;
					const rawToDate =
						typeof toolUsed.input === "object" &&
						toolUsed.input &&
						hasOwnProperty(toolUsed.input, "to_date") &&
						typeof toolUsed.input.to_date === "string"
							? toolUsed.input.to_date
							: null;
					const fromDate = rawFromDate ? parseDateAsLocal(rawFromDate) : null;
					const toDate = rawToDate ? parseDateAsLocal(rawToDate) : null;

					const fromDateDisplay =
						!!fromDate && !isNaN(fromDate.getTime()) ? meetingDateFormatter.format(fromDate) : rawFromDate;
					const toDateDisplay = !!toDate && !isNaN(toDate.getTime()) ? meetingDateFormatter.format(toDate) : rawToDate;

					const timeRangeParts = [];
					if (fromDateDisplay || toDateDisplay) {
						if (fromDateDisplay === toDateDisplay) {
							timeRangeParts.push(<> on {fromDateDisplay}</>);
						} else if (fromDateDisplay && !toDateDisplay) {
							timeRangeParts.push(<> from {fromDateDisplay} onward</>);
						} else if (!fromDateDisplay && toDateDisplay) {
							timeRangeParts.push(<> up to {toDateDisplay}</>);
						} else {
							timeRangeParts.push(
								<>
									{" "}
									from {fromDateDisplay} to {toDateDisplay}
								</>,
							);
						}
					}

					actionText = {
						thinking: <>Getting meetings{timeRangeParts}…</>,
						success: <>Got meetings{timeRangeParts}</>,
						failure: <>Getting meetings{timeRangeParts} failed</>,
					};

					break;
				}
				default:
					return null;
			}

			if (!result) {
				return (
					<ToolDetails
						loading
						summary={
							<>
								{actionText.thinking}
								{thinkingNode}
							</>
						}
					/>
				);
			}

			const toolResult = [...iterMessageContent(result.contentJson)].find(isToolResultMessageContent);
			if (!toolResult) return null;
			if (toolResult.is_error) {
				return (
					<ToolDetails
						summary={
							<>
								{actionText.failure}
								{thinkingNode}
							</>
						}
					/>
				);
			}

			const results = parseSearchMeetingsResult(toolResult.content);
			return (
				<ToolDetails
					summary={
						<>
							{actionText.success}
							{thinkingNode}
						</>
					}
				>
					<ul className={styles.meetingSearchResultList}>
						{results.map(({ meetingTitle, meetingStartTime, quotes, summaries, recapHighlights }, i) => {
							const hasContent =
								(quotes && quotes.length > 0) ||
								(summaries && summaries.length > 0) ||
								(recapHighlights && recapHighlights.length > 0);

							const meetingInfo = (
								<>
									{meetingTitle} on {meetingDateFormatter.format(meetingStartTime)}
								</>
							);

							return (
								// eslint-disable-next-line react/no-array-index-key
								<li key={i}>
									{hasContent ? (
										<DetailsSummary summary={meetingInfo}>
											{quotes && quotes.length > 0 && (
												<ul className={styles.quoteList}>
													{quotes.map(quote => (
														<li key={quote}>
															<blockquote className={styles.transcriptQuote}>{quote}</blockquote>
														</li>
													))}
												</ul>
											)}
											{summaries && summaries.length > 0 && (
												<ul className={styles.summaryList}>
													{summaries.map(summary => (
														<li key={summary}>
															<blockquote className={styles.summaryQuote}>{summary}</blockquote>
														</li>
													))}
												</ul>
											)}
											{recapHighlights && recapHighlights.length > 0 && (
												<ul className={styles.recapHighlightList}>
													{recapHighlights.map(recapHighlight => (
														<li key={recapHighlight}>
															<blockquote className={styles.recapHighlight}>{recapHighlight}</blockquote>
														</li>
													))}
												</ul>
											)}
										</DetailsSummary>
									) : (
										<p className={styles.summary}>{meetingInfo}</p>
									)}
								</li>
							);
						})}
					</ul>
				</ToolDetails>
			);
		}

		default:
			// TODO: add more code here to support any new tools added
			return null;
	}
}

function ToolsUsed({
	tools,
}: {
	tools: readonly {
		use: AskCopilotPanel_ToolUseAndResult_use$key;
		result: AskCopilotPanel_ToolUseAndResult_result$key | undefined;
	}[];
}) {
	if (!tools.length) return null;

	return (
		<ul className={styles.toolUseList}>
			{tools.map(({ use, result }, i) => (
				// eslint-disable-next-line react/no-array-index-key
				<li key={i}>
					<ToolUseAndResult use={use} result={result} />
				</li>
			))}
		</ul>
	);
}

function EmptyState({
	conversation: conversationKey,
	sendMessage,
}: {
	conversation: AskCopilotPanel_EmptyState_conversation$key;
	sendMessage: (content: string, messageId: string, contentForDisplay?: string) => void;
}) {
	const theme = React.useContext(ThemeContext);

	const conversation = useFragment(
		graphql`
			fragment AskCopilotPanel_EmptyState_conversation on AskCopilotConversation {
				suggestedQuestions {
					id
					...AskCopilotPanel_SuggestedQuestionButton_suggestedQuestion
				}
			}
		`,
		conversationKey,
	);

	return (
		<div className={styles.emptyState}>
			<img
				src={theme.isDarkMode ? emptyStateIllustrationDark : emptyStateIllustration}
				className={styles.emptyStateIllustration}
			/>
			<h1 className={styles.emptyStateHeading}>Ask Fellow a question</h1>
			<p className={styles.emptyStateSubheading}>Your AI meeting assistant can help if needed</p>
			<div className={styles.emptyStateSuggestedQuestions}>
				{conversation.suggestedQuestions.map(question => (
					<SuggestedQuestionButton key={question.id} suggestedQuestion={question} sendMessage={sendMessage} />
				))}
			</div>
		</div>
	);
}

export function AskCopilotPanelInner({
	conversation: conversationKey,
	viewer: viewerKey,
	onClose,
	className,
	noHeader,
	useModalStyles,
}: {
	conversation: AskCopilotPanelInner_conversation$key;
	viewer: AskCopilotPanelInner_viewer$key;
	onClose?: () => void;
	className?: string;
	noHeader?: boolean;
	useModalStyles?: boolean;
}) {
	const conversation = useFragment(
		graphql`
			fragment AskCopilotPanelInner_conversation on AskCopilotConversation {
				id
				...AskCopilotPanelFragment @relay(mask: false)
				...AskCopilotPanelConversationListener_conversation
				...AskCopilotPanel_useAskCopilotSendMessage_conversation
				...AskCopilotPanel_EmptyState_conversation
				...AskCopilotPanel_ChatMessage_conversation
			}
		`,
		conversationKey,
	);

	const viewer = useFragment(
		graphql`
			fragment AskCopilotPanelInner_viewer on Viewer {
				...AskCopilotPanel_ChatMessage_viewer
			}
		`,
		viewerKey,
	);

	const { messages, suggestedQuestions } = conversation ?? {};
	const latestCompleteAssistantMessage =
		messages && findLast(messages, m => m.role === "ASSISTANT" && !m.pending && m.isResponse);
	const latestMessage = messages?.[messages.length - 1];
	const pendingMessage = latestMessage?.pending ? latestMessage : null;
	const latestUserMessageHasResponse = (() => {
		function isToolUseOrResultMessage(message: { contentJson: unknown }) {
			for (const item of iterMessageContent(message.contentJson)) {
				if (item.type === "tool_use" || item.type === "tool_result") return true;
			}
			return false;
		}

		for (let i = messages.length - 1; i >= 0; i--) {
			const message = messages[i];
			if (message.role === "USER" && !isToolUseOrResultMessage(message)) {
				return false;
			} else if (message.role === "ASSISTANT" && message.isResponse && !isToolUseOrResultMessage(message)) {
				return true;
			}
		}
		// If the function didn't return in the loop that means there was no user
		// message at all; in that case we shouldn't show that the AI is generating.
		return true;
	})();
	const messageContainerRef = React.useRef<HTMLDivElement>(null);

	const [autoScroll, setAutoScroll] = React.useState(true);
	const currentlyAutoScrolling = React.useRef(false);

	React.useEffect(() => {
		const messageContainer = messageContainerRef.current;
		if (!messageContainer) return;

		function onScroll() {
			if (!messageContainer) return;
			if (currentlyAutoScrolling.current) {
				currentlyAutoScrolling.current = false;
			} else {
				setAutoScroll(messageContainer.scrollTop === messageContainer.scrollHeight - messageContainer.clientHeight);
			}
		}

		messageContainer.addEventListener("scroll", onScroll);
		return () => {
			messageContainer.removeEventListener("scroll", onScroll);
		};
	}, []);

	const scrollToBottom = React.useCallback(() => {
		const messageContainer = messageContainerRef.current;
		if (messageContainer) {
			currentlyAutoScrolling.current = true;
			messageContainer.scrollTop = messageContainer.scrollHeight;
		}
	}, []);

	// Scroll to the bottom initially
	React.useEffect(() => {
		setTimeout(scrollToBottom, 0);
	}, [scrollToBottom]);

	const messagePartListener = React.useCallback(() => {
		if (autoScroll) {
			scrollToBottom();
		}
	}, [scrollToBottom, autoScroll]);

	useAskCopilotConversationListener(conversation, messagePartListener);

	const [_sendMessage] = useAskCopilotSendMessageViaConversation(conversation);
	const sendMessage = React.useCallback(
		(content: string, messageId?: string, contentForDisplay?: string) => {
			_sendMessage(content, messageId, contentForDisplay).then(() => {
				if (autoScroll) scrollToBottom();
			});
		},
		[_sendMessage, scrollToBottom, autoScroll],
	);

	const onStopGenerating = React.useCallback(() => {
		if (!pendingMessage) return;
		commitMutation<AskCopilotPanelStopGeneratingMutation>(environment, {
			mutation: graphql`
				mutation AskCopilotPanelStopGeneratingMutation($messageId: ID!) {
					askCopilotStopGenerating(messageId: $messageId) {
						conversation {
							...AskCopilotPanelFragment
						}
					}
				}
			`,
			variables: {
				messageId: pendingMessage.id,
			},
		});
	}, [pendingMessage]);

	const dispatchAnalyticsTrack = useAnalyticsTrack();

	const onMessageFormSubmit = React.useCallback(
		(messageContent: string) => {
			sendMessage(messageContent);
			dispatchAnalyticsTrack("[Ask Copilot] Sent message", {
				// eslint-disable-next-line camelcase
				conversation_id: conversation?.id,
			});
		},
		[sendMessage, dispatchAnalyticsTrack, conversation],
	);

	const onCloseClick = React.useCallback(() => {
		onClose?.();
	}, [onClose]);

	const aiFeaturesDisabled = !React.useContext(AppContext).account?.allowAiFeatures;

	if (!useModalStyles && (!messages || !suggestedQuestions)) return null;

	return (
		<div className={cx(styles.askCopilotPanel, { [styles.modalMode]: useModalStyles }, className)}>
			{noHeader || (
				<div className={styles.header}>
					<Sparkles />
					<h2 className={styles.heading}>Ask Fellow</h2>
					<BetaLabel className={styles.betaLabel} />
					{onClose && <IconButton icon={faChevronDown} className={styles.closeButton} onClick={onCloseClick} />}
				</div>
			)}
			<div ref={messageContainerRef} className={styles.messagesContainer}>
				{messages.length === 0 ? (
					<EmptyState conversation={conversation} sendMessage={sendMessage} />
				) : (
					<>
						<p className={styles.copilotExplainerText}>
							This conversation is just between <HelpCenterLink articleId="9077100">Fellow</HelpCenterLink> and you.
						</p>

						{groupToolUsesWithResponse(messages).map((group, i) => {
							if (group.type === "toolUsesAndResults") {
								// eslint-disable-next-line react/no-array-index-key
								return <ToolsUsed key={i} tools={group.usesAndResults} />;
							}
							const { message } = group;
							return (
								<ChatMessage
									key={message.id}
									conversation={conversation}
									message={message}
									viewer={viewer}
									showDisclaimer={message === latestCompleteAssistantMessage}
									hideButtons={i === 0 || !!message.contentJson}
									userAvatarOnLeft={useModalStyles}
								/>
							);
						})}
						{!useModalStyles &&
							suggestedQuestions.map(question => (
								<SuggestedQuestionButton key={question.id} suggestedQuestion={question} sendMessage={sendMessage} />
							))}
					</>
				)}
			</div>
			<MessageForm
				conversationId={conversation.id}
				onSubmit={onMessageFormSubmit}
				currentlyGenerating={!!pendingMessage}
				onStop={onStopGenerating}
				reachedMessageLimit={conversation?.reachedMessageLimit ?? false}
				disabled={aiFeaturesDisabled}
				useModalStyles={!!useModalStyles}
				typingIndicator={
					pendingMessage?.content
						? "Copilot is typing…"
						: pendingMessage || (!pendingMessage && !latestUserMessageHasResponse)
							? "Gathering insights…"
							: undefined
				}
			/>
		</div>
	);
}

type PENDING_MESSAGE_STORE = Record<string, { updated: string; content: string }>;
const PENDING_ASK_COPILOT_MESSAGE_KEY = "pending_ask_copilot_message";
const PENDING_ASK_COPILOT_MESSAGE_EXPIRY_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

function savePendingMessage(conversationId: string, value: string) {
	const pendingMessages: PENDING_MESSAGE_STORE = store.get(PENDING_ASK_COPILOT_MESSAGE_KEY, {});

	const pendingMessageInConversation = { ...(pendingMessages[conversationId] ?? {}) };
	pendingMessageInConversation.updated = new Date().toISOString();
	pendingMessageInConversation.content = value;
	pendingMessages[conversationId] = pendingMessageInConversation;

	store.set(PENDING_ASK_COPILOT_MESSAGE_KEY, pendingMessages);
}

function getPendingMessage(conversationId: string) {
	const pendingMessages: PENDING_MESSAGE_STORE = store.get(PENDING_ASK_COPILOT_MESSAGE_KEY, {});

	const pendingMessageInConversation = pendingMessages[conversationId];
	if (!pendingMessageInConversation) return null;

	const updated = new Date(pendingMessageInConversation.updated);
	const now = new Date();
	if (now.getTime() - updated.getTime() > PENDING_ASK_COPILOT_MESSAGE_EXPIRY_MS) {
		delete pendingMessages[conversationId];
		store.set(PENDING_ASK_COPILOT_MESSAGE_KEY, pendingMessages);
		return null;
	}

	return pendingMessageInConversation.content;
}

function clearPendingMessage(conversationId: string) {
	const pendingMessages: PENDING_MESSAGE_STORE = store.get(PENDING_ASK_COPILOT_MESSAGE_KEY, {});

	delete pendingMessages[conversationId];
	store.set(PENDING_ASK_COPILOT_MESSAGE_KEY, pendingMessages);
}

function MessageForm({
	onSubmit,
	disabled,
	onStop,
	currentlyGenerating,
	reachedMessageLimit,
	useModalStyles,
	typingIndicator,
	conversationId,
}: {
	onSubmit: (content: string) => void;
	disabled?: boolean;
	onStop: () => void;
	currentlyGenerating: boolean;
	reachedMessageLimit: boolean;
	useModalStyles: boolean;
	typingIndicator: string | undefined;
	conversationId: string;
}) {
	const [messageContent, setMessageContent] = React.useState(getPendingMessage(conversationId) ?? "");

	const messageContentRef = React.useRef(messageContent);
	messageContentRef.current = messageContent;

	const messageIsEmpty = !messageContent.trim();
	const onMessageFormSubmit = React.useCallback(
		(e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			if (currentlyGenerating || messageIsEmpty) return;
			onSubmit(messageContentRef.current);
			setMessageContent("");
			clearPendingMessage(conversationId);
		},
		[onSubmit, currentlyGenerating, messageIsEmpty, conversationId],
	);

	const onMessageContentChange = React.useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			const newMessageContent = e.target.value;
			setMessageContent(newMessageContent);
			savePendingMessage(conversationId, newMessageContent);
		},
		[conversationId],
	);

	const virtualKeyboardDetector = React.useMemo(() => new VirtualKeyboardDetector(), []);
	React.useEffect(() => {
		window.addEventListener("keydown", virtualKeyboardDetector.onKeyDown);
		window.addEventListener("keyup", virtualKeyboardDetector.onKeyUp);

		return () => {
			window.removeEventListener("keydown", virtualKeyboardDetector.onKeyDown);
			window.removeEventListener("keyup", virtualKeyboardDetector.onKeyUp);
		};
	}, [virtualKeyboardDetector]);

	const textareaId = useID();

	// All this is so we can only show the outline around the input if the user
	// tabbed in (same as Discord). Normally for this purpose one would use
	// :focus-visible, but that will show the outline on the textarea whenever
	// it's focused, regardless of how focus got there.
	const [focusedViaTab, setFocusedViaTab] = React.useState(false);
	React.useEffect(() => {
		function onKeyUp(e: KeyboardEvent) {
			if (e.key === "Tab" && document.querySelector(`#${textareaId}:focus`)) {
				setFocusedViaTab(true);
			}
		}

		window.addEventListener("keyup", onKeyUp);
		return () => {
			window.removeEventListener("keyup", onKeyUp);
		};
	}, [textareaId]);

	const onTextAreaBlur = React.useCallback(() => {
		setFocusedViaTab(false);
	}, []);

	const formRef = React.useRef<HTMLFormElement>(null);
	const onTextAreaKeyDown = React.useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === "Enter" && !e.shiftKey && !virtualKeyboardDetector.isVirtualKeyboard()) {
				e.preventDefault();
				formRef.current?.requestSubmit();
			}
		},
		[virtualKeyboardDetector],
	);

	let placeholder;
	if (!React.useContext(AppContext).account?.allowAiFeatures) {
		placeholder = "Your workspace admin has disabled AI features";
	} else if (reachedMessageLimit) {
		placeholder = "You have asked the maximum number of questions";
	} else if (useModalStyles) {
		placeholder = "Ask Fellow a question…";
	} else {
		placeholder = "Ask a question about this meeting…";
	}

	const textAreaRef = React.useRef<HTMLTextAreaElement>(null);

	React.useEffect(() => {
		if (textAreaRef.current) {
			textAreaRef.current.focus();
			let end = textAreaRef.current.value.length;
			textAreaRef.current.setSelectionRange(end, end);
		}
	}, [conversationId]);

	const form = (
		<form
			ref={formRef}
			onSubmit={onMessageFormSubmit}
			className={cx(styles.inputContainer, { [styles.focusedViaTab]: focusedViaTab })}
		>
			<TextAreaAutoSize
				id={textareaId}
				ref={textAreaRef}
				className={styles.input}
				placeholder={placeholder}
				value={messageContent}
				onChange={onMessageContentChange}
				onBlur={onTextAreaBlur}
				onKeyDown={onTextAreaKeyDown}
				rows={1}
				disabled={disabled || reachedMessageLimit}
			/>
			{currentlyGenerating ? (
				<IconButton
					type="button"
					variant="primary-ai"
					className={styles.stopGeneratingButton}
					icon={faSquare}
					onClick={onStop}
				/>
			) : (
				(!messageIsEmpty || useModalStyles) && (
					<div className={styles.messageFormButtonContainer}>
						<IconButton
							type="submit"
							variant="primary-ai"
							className={styles.sendButton}
							disabled={disabled || (useModalStyles && messageIsEmpty)}
							icon={faCheck}
						/>
					</div>
				)
			)}
		</form>
	);

	const typingIndicatorNode = typingIndicator && (
		<p className={styles.typingIndicator}>
			<Sparkles className={styles.typingIndicatorIcon} />
			{typingIndicator}
		</p>
	);

	if (!useModalStyles) {
		return (
			<>
				{typingIndicatorNode}
				{form}
			</>
		);
	}

	return (
		<div style={{ padding: typingIndicatorNode ? "0 0.75rem 0.75rem" : "1.25rem 0.75rem 0.75rem" }}>
			{typingIndicatorNode}
			{form}
		</div>
	);
}
